/**
 * Whether a signed-in player may pass the email-verification gate.
 *
 * The rule lives here rather than inside `main.ts` because importing `main.ts`
 * boots the entire app — there is no way to exercise it from a test while it is
 * buried in the router — and this rule has already shipped wrong once:
 *
 *   register → /check-email → open the emailed link → /verify-email → "verified,
 *   you can now sign in" → that anchor is same-origin, so the SPA router follows
 *   it WITHOUT a page load → /login sees a signed-in user whose CACHED
 *   `emailVerified` still says false (it is only ever set by `bootstrapSession()`
 *   at boot) → /check-email, a screen whose only two buttons are "resend" and
 *   "sign out".
 *
 * A player who had just done exactly what we asked was parked on "check your
 * inbox" with no way into the game. It never appeared in dev because the seeded
 * accounts are created already verified.
 *
 * So a cached NO is not trusted on its own: the server is asked, at most once
 * every `RECHECK_MS`. The throttle is what makes it terminate — the re-render
 * that follows a re-check falls through to `bounce`.
 */
import { api } from '../core/network/api';
import { authStore, type AuthUser } from '../core/state/auth-store';

export type VerifyDecision = 'proceed' | 'recheck' | 'bounce';

/** How long a "not verified" answer from the server is trusted for. */
export const RECHECK_MS = 5_000;

export function verifyDecision(
  emailVerified: boolean,
  now: number,
  lastCheckedAt: number,
): VerifyDecision {
  if (emailVerified) return 'proceed';
  // `now - lastCheckedAt` rather than a boolean flag: the gate must keep
  // self-healing. A player who verifies in another tab minutes later gets a fresh
  // answer the next time they try the door, not the one cached at boot.
  return now - lastCheckedAt > RECHECK_MS ? 'recheck' : 'bounce';
}


/**
 * Re-reads the signed-in player from the server and updates the store.
 *
 * `GET /auth/me` rather than `bootstrapSession()`, which is what this used to
 * call: bootstrap goes through `POST /auth/refresh`, and refresh ROTATES the
 * token — polling it every few seconds would mint and destroy a token per tick
 * and race the rotation grace window for no reason. `/auth/me` is a read, and the
 * JWT strategy re-reads the user row on every request, so its `emailVerified` is
 * as fresh as the database.
 *
 * Returns false when there is no usable session (an expired link, a visitor
 * opening someone else's mail) — never throws at the caller.
 */
export async function refreshSessionUser(): Promise<boolean> {
  try {
    const { user } = await api.get<{ user: AuthUser }>('/auth/me');
    authStore.set({ ...authStore.get(), user, status: 'authenticated', error: null });
    return Boolean(user?.emailVerified);
  } catch {
    return false;
  }
}
